import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { readFromFBDB } from "../../services/fbdb.data";

// Base URL for the dailystats-modularise service (team management API)
const _teamApiUrl = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:5007';
if (!process.env.REACT_APP_API_BASE_URL) {
  console.warn('[authSlice] REACT_APP_API_BASE_URL is not set — falling back to http://127.0.0.1:5007 (local dev only). Set this env var in production!');
}
export const TEAM_API_URL = _teamApiUrl;

const initialState = {
    username: null,
    email: null,
    isUserFromShopify: false,
    state: 'idle',
    error: null,
    userStatus: {},
    accountInfo: {},
    // RBAC
    role: null,         // 'owner' | 'admin' | 'editor' | 'viewer' | null (loading)
    orgMembers: [],     // array of { email, role, addedBy, addedAt } for Team Management UI
    clientName: null,   // resolved organization name (from user_orgs map, or defaults to email prefix)
}


export const graphStatus = createAsyncThunk(
    "auth/status",
    async (user, { rejectWithValue }) => {
      try {
        const dbpath = `${user}/graph-status`;
        const response = await readFromFBDB(dbpath);
        return response.data;
      } catch (error) {
        return rejectWithValue(error);
      }
    }
);
  
export const fetchUserAccountInfo = createAsyncThunk(
  "userDesigns/account",
  async (user, { rejectWithValue }) => {
    try {
      const dbpath = `${user}/graph-account-info`;
      const response = await readFromFBDB(dbpath);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Fetch the clientName (organization) AND role for the logged-in email.
 * Replaces the old two-step Firebase RTDB fetchClientName + fetchUserRole.
 * Calls the new MongoDB-backed /api/user-role endpoint.
 */
export const fetchClientName = createAsyncThunk(
  "auth/fetchClientName",
  async (email, { rejectWithValue }) => {
    try {
      const res = await fetch(`${TEAM_API_URL}/api/user-role?email=${encodeURIComponent(email)}`);
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to fetch client name');
      // Return the full object so both clientName and role are available
      return json.clientName || email.split('@')[0];
    } catch (error) {
      console.error("fetchClientName error:", error);
      return email.split('@')[0];
    }
  }
);

/**
 * Fetch the current user's role from MongoDB.
 * Preserves the admin backdoor for support team access.
 * Calls the new /api/user-role endpoint which handles auto-owner bootstrapping.
 */
export const fetchUserRole = createAsyncThunk(
  "auth/fetchRole",
  async ({ clientName, email }, { rejectWithValue }) => {
    try {
      // BACKDOOR: If logged in via /admin/login, bypass DB check and grant Owner
      if (sessionStorage.getItem('userType') === 'admin') {
        return { role: 'owner', orgMembers: [] };
      }

      const res = await fetch(`${TEAM_API_URL}/api/user-role?email=${encodeURIComponent(email)}`);
      const json = await res.json();

      if (!res.ok) throw new Error(json.error || 'Failed to fetch role');

      return { role: json.role || 'denied', orgMembers: [] };
    } catch (error) {
      console.error("fetchUserRole error:", error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Fetch all members of an org — used by the Team Management UI.
 * Now reads from MongoDB instead of Firebase RTDB.
 */
export const fetchOrgMembers = createAsyncThunk(
  "auth/fetchOrgMembers",
  async (clientName, { rejectWithValue }) => {
    try {
      const res = await fetch(`${TEAM_API_URL}/api/team/${encodeURIComponent(clientName)}`);
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to fetch org members');
      return json.data || [];
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers:  {
        setLogInUser: (state, action) => {

          // let temp_username = action.payload || null;
          // if(temp_username && temp_username.includes("_prod")){
          //   temp_username = temp_username.split("_prod")[0];
          //   temp_username+= "-prod@causalfunnel.com";
          // }
          state.email = action.payload;
          state.username = action.payload;
        },
        setUserFromShopify: (state, action) => {
            state.isUserFromShopify = action.payload;
        },
        resetAuthSlice: (state,action) => {
          return { ...initialState };
      },
        setCheckoutLabel: (state, action) => {
            state.accountInfo.checkoutLabel = action.payload;
        },
        setUserRole: (state, action) => {
            state.role = action.payload;
        },
        clearRole: (state) => {
            state.role = null;
            state.orgMembers = [];
        },
    },
    extraReducers: (builder) => {
        builder
          .addCase(graphStatus.pending, (state) => {
            state.status = "loading";
          })
          .addCase(graphStatus.fulfilled, (state, action) => {
            state.status = "idle";
            state.error = null;
            state.userStatus = action.payload;
          })
          .addCase(graphStatus.rejected, (state, action) => {
            state.status = "idle";
            state.error = action.payload?.error;
          })
          .addCase(fetchUserAccountInfo.pending, (state) => {
            state.status = "loading";
          })
          .addCase(fetchUserAccountInfo.fulfilled, (state, action) => {
            state.status = "idle";
            state.accountInfo = action.payload;
            state.accountInfo.websiteURL = action.payload?.website;
          })
          .addCase(fetchUserAccountInfo.rejected, (state, action) => {
            state.status = "idle";
            state.error = action.payload?.error;
          })
          // fetchUserRole
          .addCase(fetchUserRole.fulfilled, (state, action) => {
            state.role = action.payload?.role || null;
          })
          .addCase(fetchUserRole.rejected, (state) => {
            state.role = 'denied';
          })
          // fetchOrgMembers
          .addCase(fetchOrgMembers.fulfilled, (state, action) => {
            state.orgMembers = action.payload || [];
          })
          .addCase(fetchOrgMembers.rejected, (state) => {
            // If the node doesn't exist (e.g., last member was removed), clear the list
            state.orgMembers = [];
          })
          // fetchClientName
          .addCase(fetchClientName.fulfilled, (state, action) => {
            state.clientName = action.payload;
          })
      },
    
})

export const { setLogInUser, setUserFromShopify, resetAuthSlice, setCheckoutLabel, setUserRole, clearRole } = authSlice.actions;

// This tricks the 50+ existing components into parsing the correct clientName without rewriting them all
export const selectLogInUsername = (state) => {
    if (state.auth.clientName && state.auth.username) {
        return `${state.auth.clientName}@causalfunnel.com`;
    }
    return state.auth.username;
};
export const selectRealSessionEmail = (state) => state.auth.username;
export const selectIsUserFromShopify = (state) => state.auth.isUserFromShopify;
export const selectUserStatus = (state) => state.auth.userStatus;
export const SelectUserWebsiteURL = (state) => state.auth.accountInfo.websiteURL;
export const SelectUserAccountInfo = (state) => state.auth.accountInfo;
export const SelectUserEmail = (state) => state.auth.email;
export const selectUserRole = (state) => state.auth.role;
export const selectOrgMembers = (state) => state.auth.orgMembers;
export const selectClientName = (state) => state.auth.clientName;

export default authSlice.reducer;